// Slice 12 — BesideU Admin organisations tab (DESIGN §6.6).
// Status filter + name/safety-contact search. Mutations (suspend, reactivate,
// approve, reject, reassign safety contact) stay inline on the row so the
// reviewer can act without losing the table context.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import type {
  AdminHrAdminCandidate,
  AdminOrgSummary,
  CreateOrgRequest,
  OrgLifecycleStatus,
} from '@besideu/shared/admin';
import { CreateOrgSchema, ORG_LIFECYCLE_STATUSES } from '@besideu/shared/admin';
import { HEADCOUNT_BANDS, type HeadcountBand } from '@besideu/shared/signup';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Field, TextInput, Select } from '@/components/ui/Field.tsx';

export function AdminOrgs() {
  const orgs = useQuery({
    queryKey: ['admin', 'orgs'],
    queryFn: () => api.get<{ orgs: AdminOrgSummary[] }>('/api/admin/orgs'),
  });

  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = (searchParams.get('status') ?? 'all') as 'all' | OrgLifecycleStatus;
  const queryText = searchParams.get('q') ?? '';
  const [provisioning, setProvisioning] = useState(false);

  function setStatus(next: 'all' | OrgLifecycleStatus) {
    const sp = new URLSearchParams(searchParams);
    if (next === 'all') sp.delete('status');
    else sp.set('status', next);
    setSearchParams(sp, { replace: true });
  }
  function setQuery(next: string) {
    const sp = new URLSearchParams(searchParams);
    if (!next) sp.delete('q');
    else sp.set('q', next);
    setSearchParams(sp, { replace: true });
  }

  const filtered = useMemo(() => {
    const all = orgs.data?.orgs ?? [];
    const q = queryText.trim().toLowerCase();
    return all.filter((o) => {
      if (statusFilter !== 'all' && o.status !== statusFilter) return false;
      if (!q) return true;
      const haystacks = [o.name, o.hrSafetyContactName ?? ''];
      return haystacks.some((h) => h.toLowerCase().includes(q));
    });
  }, [orgs.data, statusFilter, queryText]);

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Organisations</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Provision new orgs, approve pending sign-ups, suspend or reactivate orgs, and reassign
            safety contacts.
          </p>
        </div>
        <Button
          variant="primary"
          className="shrink-0"
          onClick={() => setProvisioning((v) => !v)}
          aria-expanded={provisioning}
        >
          {provisioning ? 'Cancel' : 'Provision org'}
        </Button>
      </header>

      {provisioning ? (
        <ProvisionOrgPanel
          onDone={async () => {
            setProvisioning(false);
            await queryClient.invalidateQueries({ queryKey: ['admin', 'orgs'] });
            await queryClient.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
          }}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1 rounded-md border border-border bg-background p-1">
          <FilterChip
            active={statusFilter === 'all'}
            onClick={() => setStatus('all')}
            label="All"
            count={orgs.data?.orgs.length}
          />
          {ORG_LIFECYCLE_STATUSES.map((s) => (
            <FilterChip
              key={s}
              active={statusFilter === s}
              onClick={() => setStatus(s)}
              label={s}
              count={orgs.data?.orgs.filter((o) => o.status === s).length}
            />
          ))}
        </div>
        <input
          type="search"
          value={queryText}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name or safety contact"
          className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none sm:max-w-xs"
        />
      </div>

      {orgs.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : orgs.error ? (
        <p className="text-sm text-traffic-red">Could not load orgs.</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
          {orgs.data.orgs.length === 0
            ? 'No organisations yet.'
            : 'No organisations match the current filters.'}
        </div>
      ) : (
        <OrgsTable orgs={filtered} />
      )}
    </section>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number | undefined;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded px-3 py-1.5 text-xs font-medium transition',
        active
          ? 'bg-foreground text-background'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      ].join(' ')}
    >
      {label}
      {typeof count === 'number' ? (
        <span className={`ml-1.5 ${active ? 'opacity-70' : 'opacity-60'}`}>{count}</span>
      ) : null}
    </button>
  );
}

function OrgsTable({ orgs }: { orgs: AdminOrgSummary[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-muted-foreground">
          <tr>
            <th className="px-4 py-2 font-medium">Name</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th className="px-4 py-2 font-medium">HR / Mgr / Emp</th>
            <th className="px-4 py-2 font-medium">Cases (open · closed)</th>
            <th className="px-4 py-2 font-medium">DPA</th>
            <th className="px-4 py-2 font-medium">Safety Contact</th>
            <th className="px-4 py-2 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {orgs.map((o) => (
            <OrgRow key={o.id} org={o} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrgRow({ org }: { org: AdminOrgSummary }) {
  const queryClient = useQueryClient();
  const [reassigning, setReassigning] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sent'>('idle');
  const [resendError, setResendError] = useState<string | null>(null);

  function invalidateAll() {
    void queryClient.invalidateQueries({ queryKey: ['admin'] });
  }

  const resendHrInvite = useMutation({
    mutationFn: () => api.post(`/api/admin/orgs/${org.id}/resend-hr-invite`),
    onSuccess: () => {
      setResendError(null);
      setResendStatus('sent');
      // Refresh the roster + audit log so the resend event + any state change
      // surface without a manual reload (parity with suspend/approve/reject).
      invalidateAll();
      window.setTimeout(() => setResendStatus('idle'), 3000);
    },
    onError: (e) => {
      setResendError(e instanceof ApiError ? translateResendError(e) : 'Resend failed.');
    },
  });

  const suspend = useMutation({
    mutationFn: () => api.post(`/api/admin/orgs/${org.id}/suspend`),
    onSuccess: invalidateAll,
  });
  const reactivate = useMutation({
    mutationFn: () => api.post(`/api/admin/orgs/${org.id}/reactivate`),
    onSuccess: invalidateAll,
  });
  const approve = useMutation({
    mutationFn: () => api.post(`/api/admin/orgs/${org.id}/approve`),
    onSuccess: invalidateAll,
  });
  const reject = useMutation({
    mutationFn: () => api.post(`/api/admin/orgs/${org.id}/reject`),
    onSuccess: invalidateAll,
  });

  function confirmThen(message: string, run: () => void) {
    if (typeof window !== 'undefined' && window.confirm(message)) run();
  }

  return (
    <>
      <tr className="border-t border-border align-top hover:bg-muted/20">
        <td className="px-4 py-2 font-medium">
          <Link to={`/admin/orgs/${org.id}`} className="underline-offset-2 hover:underline">
            {org.name}
          </Link>
        </td>
        <td className="px-4 py-2">
          {org.awaitingHrOnboarding ? (
            <span className="inline-flex w-fit items-center rounded-full bg-traffic-amber/20 px-2 py-0.5 text-xs font-medium whitespace-nowrap text-traffic-amber-text">
              Awaiting HR onboarding
            </span>
          ) : (
            <StatusPill status={org.status} />
          )}
        </td>
        <td className="px-4 py-2 text-muted-foreground">
          {org.hrAdminCount} · {org.managerCount} · {org.employeeCount}
        </td>
        <td className="px-4 py-2 text-muted-foreground">
          {org.caseCountOpen} · {org.caseCountClosed}
        </td>
        <td className="px-4 py-2 text-muted-foreground">
          {org.dpaVersionAccepted ? (
            <span title={org.dpaAcceptedAt ?? ''}>{org.dpaVersionAccepted}</span>
          ) : (
            '—'
          )}
        </td>
        <td className="px-4 py-2 text-muted-foreground">{org.hrSafetyContactName ?? '—'}</td>
        <td className="px-4 py-2">
          <div className="flex flex-wrap gap-2">
            {org.status === 'Pending' && org.awaitingHrOnboarding ? (
              // Admin-provisioned, waiting on the HR Admin's first sign-in.
              // Approve can't succeed yet (DPA/safety-contact unset until they
              // onboard), so we offer Resend invite + Reject instead.
              <>
                {resendStatus === 'sent' ? (
                  <span className="px-2 py-1 text-xs text-traffic-green-text" role="status">
                    Sent
                  </span>
                ) : (
                  <Button
                    variant="secondary"
                    className="px-2 py-1 text-xs"
                    loading={resendHrInvite.isPending}
                    onClick={() => resendHrInvite.mutate()}
                    aria-label={`Resend HR invite for ${org.name}`}
                  >
                    Resend HR invite
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="px-2 py-1 text-xs"
                  loading={reject.isPending}
                  onClick={() =>
                    confirmThen(
                      `Reject ${org.name}? This permanently deletes the provisioned org and its invited HR Admin.`,
                      () => reject.mutate(),
                    )
                  }
                >
                  Reject
                </Button>
              </>
            ) : org.status === 'Pending' ? (
              <>
                <Button
                  variant="primary"
                  className="px-2 py-1 text-xs"
                  loading={approve.isPending}
                  onClick={() =>
                    confirmThen(`Approve ${org.name}? This activates the org.`, () =>
                      approve.mutate(),
                    )
                  }
                >
                  Approve
                </Button>
                <Button
                  variant="secondary"
                  className="px-2 py-1 text-xs"
                  loading={reject.isPending}
                  onClick={() =>
                    confirmThen(
                      `Reject ${org.name}? This permanently deletes the pending org and its users.`,
                      () => reject.mutate(),
                    )
                  }
                >
                  Reject
                </Button>
              </>
            ) : null}
            {org.status === 'Active' ? (
              <>
                <Button
                  variant="secondary"
                  className="px-2 py-1 text-xs"
                  loading={suspend.isPending}
                  onClick={() => confirmThen(`Suspend ${org.name}?`, () => suspend.mutate())}
                >
                  Suspend
                </Button>
                <Button
                  variant="ghost"
                  className="px-2 py-1 text-xs"
                  onClick={() => setReassigning((v) => !v)}
                >
                  {reassigning ? 'Cancel' : 'Reassign safety contact'}
                </Button>
              </>
            ) : null}
            {org.status === 'Suspended' ? (
              <Button
                variant="primary"
                className="px-2 py-1 text-xs"
                loading={reactivate.isPending}
                onClick={() => confirmThen(`Reactivate ${org.name}?`, () => reactivate.mutate())}
              >
                Reactivate
              </Button>
            ) : null}
          </div>
        </td>
      </tr>
      {resendError ? (
        <tr>
          <td
            colSpan={7}
            className="border-t border-border bg-traffic-red/5 px-4 py-2 text-xs text-traffic-red"
          >
            {resendError}
          </td>
        </tr>
      ) : null}
      {reassigning ? (
        <tr className="border-t border-dashed border-border bg-muted/20">
          <td colSpan={7} className="px-4 py-3">
            <ReassignSafetyContactRow
              orgId={org.id}
              currentUserId={org.hrSafetyContactUserId}
              onDone={() => {
                setReassigning(false);
                invalidateAll();
              }}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

// Keep API error codes out of the operator-facing UI (parity with the resend
// translators in AdminAdmins.tsx).
function translateResendError(e: ApiError): string {
  switch (e.message) {
    case 'already_onboarded':
      return 'This organisation’s HR Admin has already completed onboarding.';
    case 'not_found':
      return 'Organisation no longer exists.';
    default:
      return e.message || 'Resend failed.';
  }
}

function ReassignSafetyContactRow({
  orgId,
  currentUserId,
  onDone,
}: {
  orgId: string;
  currentUserId: string | null;
  onDone: () => void;
}) {
  const [selected, setSelected] = useState('');
  const [error, setError] = useState<string | null>(null);

  const candidates = useQuery({
    queryKey: ['admin', 'orgs', orgId, 'hr-admins'],
    queryFn: () =>
      api.get<{ candidates: AdminHrAdminCandidate[] }>(`/api/admin/orgs/${orgId}/hr-admins`),
  });

  const reassign = useMutation({
    mutationFn: () => api.post(`/api/admin/orgs/${orgId}/safety-contact`, { userId: selected }),
    onSuccess: onDone,
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 400) {
        setError('That user can’t take the role (not an HR Admin in this org, or not Active).');
      } else {
        setError('Something went wrong.');
      }
    },
  });

  if (candidates.isPending) {
    return <p className="text-sm text-muted-foreground">Loading candidates…</p>;
  }
  if (candidates.error) {
    return <p className="text-sm text-traffic-red">Could not load HR Admins.</p>;
  }

  const eligible = (candidates.data?.candidates ?? []).filter((c) => c.id !== currentUserId);
  if (eligible.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No other Active HR Admins in this org. Invite a second HR Admin first via the customer’s HR
        shell.
      </p>
    );
  }

  return (
    <form
      className="flex flex-wrap items-center gap-3 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        if (selected) reassign.mutate();
      }}
    >
      <label className="flex items-center gap-2">
        Reassign to:
        <select
          className="rounded border border-border bg-background px-2 py-1"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          required
        >
          <option value="" disabled>
            Choose an HR Admin…
          </option>
          {eligible.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.email})
            </option>
          ))}
        </select>
      </label>
      <Button
        type="submit"
        variant="primary"
        className="px-2 py-1 text-xs"
        loading={reassign.isPending}
        disabled={!selected}
      >
        Confirm reassign
      </Button>
      {error ? <span className="text-traffic-red">{error}</span> : null}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Provision-org panel — shown at the top of the page when "Provision org" is
// open. Creates the org + its first HR Admin (both Pending); a magic link goes
// out and the HR Admin accepts the DPA + acknowledges safety-contact on first
// sign-in, which flips the org Active. No DPA acceptance happens here — that's
// the data controller's to make, not the operator's.
// ---------------------------------------------------------------------------

function ProvisionOrgPanel({ onDone }: { onDone: () => void }) {
  const [organizationName, setOrganizationName] = useState('');
  const [hrAdminName, setHrAdminName] = useState('');
  const [hrAdminEmail, setHrAdminEmail] = useState('');
  // PROJECT §4 Step 1 — operator may pre-fill headcount + primary/escalation
  // contacts from CRM data. All optional; the HR Admin completes anything left
  // blank at first-sign-in onboarding.
  const [headcountBand, setHeadcountBand] = useState<HeadcountBand | ''>('');
  const [primaryName, setPrimaryName] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [escalationName, setEscalationName] = useState('');
  const [escalationEmail, setEscalationEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const provision = useMutation({
    mutationFn: (input: CreateOrgRequest) =>
      api.post<{ org: AdminOrgSummary }>('/api/admin/orgs', input),
    onSuccess: () => onDone(),
    onError: (e) => {
      setError(
        e instanceof ApiError ? e.message || 'Provisioning failed.' : 'Provisioning failed.',
      );
    },
  });

  function submit() {
    setError(null);
    // PROJECT §4 Step 1 — if either contact is started, the pair must be
    // complete (a primary name without an email would be useless for
    // routing). Same rule self-imposes on escalation. Build a partial object
    // and let Zod's discriminated issues point at the right field.
    const contactOrUndefined = (n: string, e: string) =>
      n.trim() === '' && e.trim() === '' ? undefined : { name: n.trim(), email: e.trim() };

    const parsed = CreateOrgSchema.safeParse({
      organizationName,
      hrAdminName,
      hrAdminEmail,
      headcountBand: headcountBand === '' ? undefined : headcountBand,
      primaryContact: contactOrUndefined(primaryName, primaryEmail),
      escalationContact: contactOrUndefined(escalationName, escalationEmail),
    });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const field = issue?.path[0];
      setError(
        field === 'organizationName'
          ? 'Please enter an organisation name (2–120 characters).'
          : field === 'hrAdminName'
            ? 'Please enter the HR Admin’s name.'
            : field === 'hrAdminEmail'
              ? 'Please enter a valid HR Admin email address.'
              : field === 'headcountBand'
                ? 'Please pick a headcount band.'
                : field === 'primaryContact' || field === 'primaryContact.email'
                  ? 'Please enter a valid primary-contact email (or leave both primary fields blank).'
                  : field === 'escalationContact' || field === 'escalationContact.email'
                    ? 'Please enter a valid escalation-contact email (or leave both escalation fields blank).'
                    : (issue?.message ?? 'Invalid input.'),
      );
      return;
    }
    provision.mutate(parsed.data);
  }

  return (
    <div className="rounded-lg border border-border bg-muted/10 p-4">
      <h2 className="text-base font-semibold">Provision organisation</h2>
      <p className="mt-1 mb-4 text-xs text-muted-foreground">
        Creates the org and invites its first HR Admin. A magic-link email is sent on submit; the HR
        Admin accepts the Data Processing Agreement and acknowledges safety-contact responsibility
        on first sign-in, which activates the org. Headcount + contacts are optional here — the HR
        Admin completes them on first sign-in if left blank.
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Organisation name">
          {(id, desc) => (
            <TextInput
              id={id}
              type="text"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              autoComplete="off"
              {...desc}
            />
          )}
        </Field>
        <Field label="HR Admin name">
          {(id, desc) => (
            <TextInput
              id={id}
              type="text"
              value={hrAdminName}
              onChange={(e) => setHrAdminName(e.target.value)}
              autoComplete="off"
              {...desc}
            />
          )}
        </Field>
        <Field label="HR Admin email">
          {(id, desc) => (
            <TextInput
              id={id}
              type="email"
              value={hrAdminEmail}
              onChange={(e) => setHrAdminEmail(e.target.value)}
              autoComplete="off"
              {...desc}
            />
          )}
        </Field>
        <Field
          label="Headcount band"
          hint="Optional. Defaults to the HR Admin’s input on first sign-in."
        >
          {(id, desc) => (
            <Select
              id={id}
              value={headcountBand}
              onChange={(e) => setHeadcountBand(e.target.value as HeadcountBand | '')}
              {...desc}
            >
              <option value="">Not specified</option>
              {HEADCOUNT_BANDS.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <fieldset className="grid gap-2 rounded border border-border p-3">
          <legend className="px-1 text-xs font-medium text-foreground">
            Primary contact (optional)
          </legend>
          <Field label="Name">
            {(id, desc) => (
              <TextInput
                id={id}
                type="text"
                value={primaryName}
                onChange={(e) => setPrimaryName(e.target.value)}
                autoComplete="off"
                {...desc}
              />
            )}
          </Field>
          <Field label="Work email">
            {(id, desc) => (
              <TextInput
                id={id}
                type="email"
                value={primaryEmail}
                onChange={(e) => setPrimaryEmail(e.target.value)}
                autoComplete="off"
                {...desc}
              />
            )}
          </Field>
        </fieldset>
        <fieldset className="grid gap-2 rounded border border-border p-3">
          <legend className="px-1 text-xs font-medium text-foreground">
            Escalation contact (optional)
          </legend>
          <Field label="Name">
            {(id, desc) => (
              <TextInput
                id={id}
                type="text"
                value={escalationName}
                onChange={(e) => setEscalationName(e.target.value)}
                autoComplete="off"
                {...desc}
              />
            )}
          </Field>
          <Field label="Work email">
            {(id, desc) => (
              <TextInput
                id={id}
                type="email"
                value={escalationEmail}
                onChange={(e) => setEscalationEmail(e.target.value)}
                autoComplete="off"
                {...desc}
              />
            )}
          </Field>
        </fieldset>
      </div>
      {error ? (
        <p className="mt-3 text-sm text-traffic-red" role="alert">
          {error}
        </p>
      ) : null}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" onClick={submit} loading={provision.isPending}>
          Provision &amp; invite
        </Button>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: AdminOrgSummary['status'] }) {
  const className =
    status === 'Active'
      ? 'bg-traffic-green/10 text-traffic-green-text'
      : status === 'Pending'
        ? 'bg-muted-foreground/15 text-foreground'
        : 'bg-traffic-red/10 text-traffic-red';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {status}
    </span>
  );
}
